import { CommonModule } from '@angular/common';
import { Component, Injector, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Subject, forkJoin, takeUntil } from 'rxjs';
import {extractErrorMessage, goBack} from 'src/app/common/common';
import {RegisteredReportDto, RegisteredReportViewModel} from 'src/app/model/registeredReports.model';
import {ApiService, CustomHttpParams} from 'src/app/services/api.service';
import {HttpParams} from "@angular/common/http";
import {RidViewModel} from "../../../../model/rids.model";
import {LoaderComponent} from "../../../common/loader/loader.component";

@Component({
  selector: "app-create-registered-report",
  standalone: true,
  imports: [RouterModule, CommonModule, FormsModule, ReactiveFormsModule, LoaderComponent],
  templateUrl: "./create-registered-report.component.html",
  styleUrl: "./create-registered-report.component.scss",
})
export class CreateRegisteredReportComponent implements OnInit, OnDestroy {
  protected readonly editing: boolean = true; //can only edit right now
  // Forms control
  protected registeredReportForm = new FormGroup({
    reportRequestId: new FormControl<string>({ value: '', disabled: this.editing }, Validators.required),
    rids: new FormControl<string[]>([], Validators.required),
    granularity: new FormControl<string>('', Validators.required),
    reportBackDuration: new FormControl<string>('', Validators.required),
    reportIntervalDtstart: new FormControl<string>('', Validators.required),
    reportIntervalDuration: new FormControl<string>('', Validators.required),
    reportOffset: new FormControl<string | null>(null),
    sendUpdates: new FormControl<boolean>(false),
    updateType: new FormControl<string | null>(null),
    scheduledUpdateTime: new FormControl<string | null>(null),
    scheduledUpdateDuration: new FormControl<string | null>(null),
  });

  protected unsubscribe = new Subject<void>();
  protected formsSubscribe: any;

  protected isLoading: boolean = false;
  protected registeredReportId: number;
  protected router: Router;
  protected error: string | null = null;

  protected registeredReport: RegisteredReportViewModel | undefined;

  protected rids: RidViewModel[] = [];
  protected selectedRids: string[] = [];

  constructor(
    private injector: Injector,
    private registeredReportService: ApiService
  ) {
    this.router = injector.get(Router);
    this.router.events.subscribe((event: any) => {
      if (event?.snapshot?.params.id) {
        this.registeredReportId = event.snapshot.params.id;
      }
    });
  }

  setFormsDefaultValue() {
    if (!this.registeredReport) {
      return;
    }
    this.registeredReportForm.controls.updateType.setValue(this.registeredReport.update_type);
    this.registeredReportForm.controls.reportRequestId.setValue(this.registeredReport.report_request_id);

    const selectedRidIds = this.registeredReport.rids.map(rid => rid.id);
    this.registeredReportForm.controls.rids.setValue(selectedRidIds);
    this.selectedRids = selectedRidIds;

    this.registeredReportForm.controls.granularity.setValue(this.registeredReport.granularity);
    this.registeredReportForm.controls.reportBackDuration.setValue(this.registeredReport.report_back_duration);
    this.registeredReportForm.controls.reportIntervalDtstart.setValue(this.registeredReport.report_interval_dtstart);
    this.registeredReportForm.controls.reportIntervalDuration.setValue(this.registeredReport.report_interval_duration);
    this.registeredReportForm.controls.reportOffset.setValue(this.registeredReport.report_offset);
    this.registeredReportForm.controls.sendUpdates.setValue(this.registeredReport.send_updates);
    this.registeredReportForm.controls.scheduledUpdateTime.setValue(this.registeredReport.scheduled_update_time);
    this.registeredReportForm.controls.scheduledUpdateDuration.setValue(this.registeredReport.scheduled_update_duration);
  }

  ngOnInit(): void {
    const reportsQueryParams: CustomHttpParams = {
      'depth': 2,
      'id': this.registeredReportId,
    };
    const ridsQueryParams: CustomHttpParams = {
      'depth': 1,
    };
    this.isLoading = true;
    forkJoin([
      this.registeredReportService.getRids(ridsQueryParams).pipe(takeUntil(this.unsubscribe)),
      this.registeredReportService.getRegisteredReports(reportsQueryParams).pipe(takeUntil(this.unsubscribe))
    ]).subscribe({
      next: ([ridsResponse, reportsResponse]) => {
        if (ridsResponse) {
          this.rids = ridsResponse;
        }

        if (reportsResponse && reportsResponse.length > 0) {
          this.registeredReport = {
            ...this.registeredReport,
            ...reportsResponse[0]
          };

          this.setFormsDefaultValue();
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.error = extractErrorMessage(err, "Failed to load data.");
        this.isLoading = false;
      }
    });

    this.setupFormControlSubscriptions();
  }

  setupFormControlSubscriptions() {
    this.registeredReportForm.controls.rids.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => {
        if (val && Array.isArray(val)) {
          // Clean the malformed values
          this.selectedRids = val.map(value => {
            if (typeof value === 'string' && value.includes("'")) {
              const match = value.match(/'([^']+)'/);
              return match ? match[1] : value;
            }
            return value;
          });
        }
      });
  }

  save() {
    if (this.registeredReportForm.invalid) {
      this.error = 'Please fill out all required fields correctly.';
      return;
    }

    if (this.editing) {
      // Prepare data for update
      const editRegisteredReports: RegisteredReportDto = {
        id: this.registeredReport?.id!,
        granularity: this.registeredReportForm.value.granularity!,
        report_back_duration: this.registeredReportForm.value.reportBackDuration!,
        report_interval_dtstart: this.registeredReportForm.value.reportIntervalDtstart!,
        report_interval_duration: this.registeredReportForm.value.reportIntervalDuration!,
        report_offset: this.registeredReportForm.value.reportOffset || null,
        report_request_id: this.registeredReportForm.value.reportRequestId!,
        rids: this.selectedRids,
        scheduled_update_duration: this.registeredReportForm.value.scheduledUpdateDuration || null,
        scheduled_update_time: this.registeredReportForm.value.scheduledUpdateTime || null,
        send_updates: this.registeredReportForm.value.sendUpdates || false,
        update_type: this.registeredReportForm.value.updateType || null,
      };
      this.isLoading = true;
      this.registeredReportService
        .editRegisteredReports(editRegisteredReports)
        .subscribe({
          next: () => {
            this.isLoading = false;
            goBack("/registeredReports", this.router);
          },
          error: (err) => {
            console.error(err);
            this.error = extractErrorMessage(err, "Failed to update registered report.");
            this.isLoading = false;
          }
        });
    }
  }

  backPage(url: string, router: Router) {
    goBack(url, router);
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
